import { Chunk, CHUNK_SIZE, CHUNK_HEIGHT } from './Chunk';
import { Block } from './Block';

// Simple Perlin-like noise implementation
class SimplexNoise {
  private perm: number[] = [];

  constructor(seed: number) {
    const random = this.seededRandom(seed);

    // Initialize permutation table
    for (let i = 0; i < 256; i++) {
      this.perm[i] = i;
    }

    // Shuffle using seed
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [this.perm[i], this.perm[j]] = [this.perm[j], this.perm[i]];
    }

    // Duplicate for overflow
    for (let i = 0; i < 256; i++) {
      this.perm[256 + i] = this.perm[i];
    }
  }

  private seededRandom(seed: number): () => number {
    return () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
  }

  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  private lerp(a: number, b: number, t: number): number {
    return a + t * (b - a);
  }

  private grad(hash: number, x: number, y: number): number {
    const h = hash & 7;
    const u = h < 4 ? x : y;
    const v = h < 4 ? y : x;
    return ((h & 1) ? -u : u) + ((h & 2) ? -2 * v : 2 * v);
  }

  public noise2D(x: number, y: number): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;

    x -= Math.floor(x);
    y -= Math.floor(y);

    const u = this.fade(x);
    const v = this.fade(y);

    const A = this.perm[X] + Y;
    const B = this.perm[X + 1] + Y;

    return this.lerp(
      this.lerp(this.grad(this.perm[A], x, y), this.grad(this.perm[B], x - 1, y), u),
      this.lerp(this.grad(this.perm[A + 1], x, y - 1), this.grad(this.perm[B + 1], x - 1, y - 1), u),
      v
    );
  }

  public octaveNoise2D(x: number, y: number, octaves: number, persistence: number): number {
    let total = 0;
    let frequency = 1;
    let amplitude = 1;
    let maxValue = 0;

    for (let i = 0; i < octaves; i++) {
      total += this.noise2D(x * frequency, y * frequency) * amplitude;
      maxValue += amplitude;
      amplitude *= persistence;
      frequency *= 2;
    }

    return total / maxValue;
  }
}

export interface TerrainConfig {
  seed: number;
  baseHeight: number;
  heightVariation: number;
  scale: number;
  octaves: number;
  persistence: number;
  waterLevel: number;
  generateTrees: boolean;
  generateOres: boolean;
}

const DEFAULT_CONFIG: TerrainConfig = {
  seed: 12345,
  baseHeight: 8,
  heightVariation: 6,
  scale: 0.02,
  octaves: 4,
  persistence: 0.5,
  waterLevel: 4,
  generateTrees: true,
  generateOres: true,
};

export class TerrainGenerator {
  private noise: SimplexNoise;
  private treeNoise: SimplexNoise;
  private oreNoise: SimplexNoise;
  private config: TerrainConfig;

  constructor(config: Partial<TerrainConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.noise = new SimplexNoise(this.config.seed);
    this.treeNoise = new SimplexNoise(this.config.seed + 1000);
    this.oreNoise = new SimplexNoise(this.config.seed + 2000);
  }

  public generateChunk(chunk: Chunk): void {
    const { baseHeight, heightVariation, scale, octaves, persistence, waterLevel } = this.config;

    // Generate terrain height for each column
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const worldX = chunk.worldPosition.x + lx;
        const worldZ = chunk.worldPosition.z + lz;

        // Calculate height using noise
        const noiseValue = this.noise.octaveNoise2D(worldX * scale, worldZ * scale, octaves, persistence);
        const height = Math.floor(baseHeight + noiseValue * heightVariation);

        // Fill column
        this.generateColumn(chunk, lx, lz, height, waterLevel);

        // Maybe add tree
        if (this.config.generateTrees && height > waterLevel) {
          this.maybeAddTree(chunk, lx, lz, height, worldX, worldZ);
        }
      }
    }

    // Generate ores
    if (this.config.generateOres) {
      this.generateOres(chunk);
    }

    chunk.markDirty();
  }

  private generateColumn(chunk: Chunk, x: number, z: number, height: number, waterLevel: number): void {
    for (let y = 0; y < CHUNK_HEIGHT; y++) {
      let blockId: number;

      if (y === 0) {
        // Bedrock at bottom
        blockId = Block.BEDROCK;
      } else if (y < height - 3) {
        // Deep stone
        blockId = Block.STONE;
      } else if (y < height - 1) {
        // Dirt layer
        blockId = Block.DIRT;
      } else if (y < height) {
        // Surface layer
        if (height <= waterLevel + 1) {
          blockId = Block.SAND;
        } else {
          blockId = Block.GRASS;
        }
      } else if (y <= waterLevel) {
        // Water
        blockId = Block.WATER;
      } else {
        // Air
        blockId = Block.AIR;
      }

      chunk.setBlock(x, y, z, blockId);
    }
  }

  private maybeAddTree(chunk: Chunk, x: number, z: number, height: number, worldX: number, worldZ: number): void {
    // Use noise to determine tree placement
    const treeValue = this.treeNoise.noise2D(worldX * 0.3, worldZ * 0.3);

    // Only place trees in certain areas and not at chunk edges
    if (treeValue > 0.6 && x >= 2 && x < CHUNK_SIZE - 2 && z >= 2 && z < CHUNK_SIZE - 2) {
      // Random-ish tree height based on position
      const treeHeight = 4 + Math.abs(Math.floor(treeValue * 3));

      if (height + treeHeight < CHUNK_HEIGHT - 2) {
        this.generateTree(chunk, x, height, z, treeHeight);
      }
    }
  }

  private generateTree(chunk: Chunk, x: number, baseY: number, z: number, height: number): void {
    // Trunk
    for (let y = 0; y < height; y++) {
      chunk.setBlock(x, baseY + y, z, Block.WOOD);
    }

    // Leaves (simple sphere-ish shape)
    const leafStart = height - 2;
    const leafEnd = height + 1;

    for (let ly = leafStart; ly <= leafEnd; ly++) {
      const radius = ly === leafEnd ? 1 : 2;

      for (let lx = -radius; lx <= radius; lx++) {
        for (let lz = -radius; lz <= radius; lz++) {
          // Skip corners for rounder shape
          if (Math.abs(lx) === radius && Math.abs(lz) === radius && ly !== height - 1) {
            continue;
          }

          const bx = x + lx;
          const by = baseY + ly;
          const bz = z + lz;

          if (chunk.isValidPosition(bx, by, bz)) {
            const existing = chunk.getBlock(bx, by, bz);
            if (existing === Block.AIR) {
              chunk.setBlock(bx, by, bz, Block.LEAVES);
            }
          }
        }
      }
    }
  }

  private generateOres(chunk: Chunk): void {
    for (let y = 1; y < CHUNK_HEIGHT / 2; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          const block = chunk.getBlock(x, y, z);

          if (block !== Block.STONE) continue;

          const worldX = chunk.worldPosition.x + x;
          const worldZ = chunk.worldPosition.z + z;

          // Iron ore (more common, higher levels)
          if (y < 12) {
            const ironNoise = this.oreNoise.noise2D(worldX * 0.2 + y * 0.1, worldZ * 0.2);
            if (ironNoise > 0.7) {
              chunk.setBlock(x, y, z, Block.IRON);
              continue;
            }
          }

          // Gold ore (less common, lower levels)
          if (y < 8) {
            const goldNoise = this.oreNoise.noise2D(worldX * 0.15 + y * 0.2, worldZ * 0.15 + 100);
            if (goldNoise > 0.8) {
              chunk.setBlock(x, y, z, Block.GOLD);
              continue;
            }
          }

          // Diamond (rare, very low levels)
          if (y < 5) {
            const diamondNoise = this.oreNoise.noise2D(worldX * 0.1 + y * 0.3, worldZ * 0.1 + 200);
            if (diamondNoise > 0.88) {
              chunk.setBlock(x, y, z, Block.DIAMOND);
            }
          }
        }
      }
    }
  }

  public getHeightAt(worldX: number, worldZ: number): number {
    const { baseHeight, heightVariation, scale, octaves, persistence } = this.config;
    const noiseValue = this.noise.octaveNoise2D(worldX * scale, worldZ * scale, octaves, persistence);
    return Math.floor(baseHeight + noiseValue * heightVariation);
  }

  public getConfig(): TerrainConfig {
    return { ...this.config };
  }
}
